import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { featuredPropertiesService } from '../../../services/featuredPropertiesService';

const BulkOperationsPanel = ({
  selectedProperties = [],
  onBulkAction,
  onClose
}) => {
  const [operation, setOperation] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleBulkOperation = async () => {
    if (!operation || selectedProperties?.length === 0) return;

    setLoading(true);
    setError(null);

    try {
      let successCount = 0;
      let errorCount = 0;

      for (const propertyId of selectedProperties) {
        try {
          switch (operation) {
            case 'activate':
              await featuredPropertiesService?.toggleFeaturedPropertyStatus(propertyId, true);
              break;
            case 'deactivate':
              await featuredPropertiesService?.toggleFeaturedPropertyStatus(propertyId, false);
              break;
            case 'delete':
              await featuredPropertiesService?.deleteFeaturedProperty(propertyId);
              break;
            default:
              break;
          }
          successCount++;
        } catch (err) {
          errorCount++;
        }
      }

      if (errorCount > 0) {
        setError(`${errorCount} opération(s) ont échoué sur ${selectedProperties?.length}`);
      }

      // Notify parent component
      onBulkAction?.(operation);
      
      if (errorCount === 0) {
        onClose?.();
      }
    } catch (err) {
      setError(`Erreur lors de l'opération en lot: ${err?.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getOperationLabel = () => {
    switch (operation) {
      case 'activate':
        return 'Activer';
      case 'deactivate':
        return 'Désactiver';
      case 'delete':
        return 'Supprimer';
      default:
        return 'Appliquer';
    }
  };

  const getOperationIcon = () => {
    switch (operation) {
      case 'activate':
        return 'Eye';
      case 'deactivate':
        return 'EyeOff';
      case 'delete':
        return 'Trash2';
      default:
        return 'Settings';
    }
  };

  const isDestructive = operation === 'delete';

  return (
    <div className="bg-white rounded-lg shadow-warm border border-brand-earth/20 p-4 mb-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Icon name="CheckSquare" size={20} className="text-brand-earth" />
            <span className="font-medium text-text-primary">
              {selectedProperties?.length} propriété(s) sélectionnée(s)
            </span>
          </div>

          <div className="flex items-center gap-3">
            <Select
              value={operation}
              onChange={(e) => setOperation(e?.target?.value)}
              className="min-w-[160px]"
            >
              <option value="">Choisir une action</option>
              <option value="activate">Activer toutes</option>
              <option value="deactivate">Désactiver toutes</option>
              <option value="delete">Supprimer toutes</option>
            </Select>

            <Button
              variant={isDestructive ? "destructive" : "default"}
              size="sm"
              onClick={handleBulkOperation}
              disabled={!operation || loading}
              iconName={loading ? "Loader2" : getOperationIcon()}
              className={loading ? "animate-spin" : ""}
            >
              {loading ? 'Traitement...' : getOperationLabel()}
            </Button>
          </div>
        </div>

        <Button
          variant="ghost"
          size="sm"
          onClick={onClose}
          iconName="X"
        />
      </div>

      {/* Error Display */}
      {error && (
        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center">
            <Icon name="AlertCircle" size={16} className="text-red-600 mr-2" />
            <span className="text-sm text-red-800">{error}</span>
          </div>
        </div>
      )}

      {/* Confirmation for destructive actions */}
      {operation === 'delete' && (
        <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-center">
            <Icon name="AlertTriangle" size={16} className="text-yellow-600 mr-2" />
            <span className="text-sm text-yellow-800">
              ⚠️ Cette action est irréversible. Les propriétés sélectionnées seront définitivement supprimées de la liste des propriétés en vedette.
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkOperationsPanel;